import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { EmptyState, FormDialog, TableCard } from '~/components';
import { BasicGrid } from '~/components/agGrid/Grid';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { useCertificationRef } from '../account/vaultSelectors';
import { CertForm } from './CertForm';
import { createCertCols } from './constants';
import { CertFormValues } from './types';

const TAGS = CT.certs.table;

const CertNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.certifications}
    title="No certifications yet"
    cyTag={TAGS.emptyState}
  />
);

export const CertificationDetails = () => {
  const { certRef } = useCertificationRef();
  if (!certRef) {
    return null;
  }

  const { issuerOptions } = useSelector(certRef, (state) => state.context);
  const certCrudRef = useSelector(certRef, (state) => state.context.crudRef);
  const { data, loading: crudLoading, form } = useSelector(certCrudRef, (state) => state.context);
  const { create, edit } = useSelector(certCrudRef, (state) => state.context.modal);

  const handleOpen = () => {
    certCrudRef.send({ type: 'ON_ADD' });
  };
  const handleClose = () => {
    certCrudRef.send({ type: 'ON_CLOSE_MODAL' });
  };

  const handleDelete = (id: number) => {
    certCrudRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } });
  };

  const handleEdit = (id: number) => {
    certCrudRef.send({ type: 'ON_EDIT', payload: { id } });
  };

  const editCert = (values: CertFormValues) => {
    certCrudRef.send({ type: 'ON_UPDATE', values: values });
  };

  const createCert = (values: CertFormValues) => {
    if (values.expiryDate === '') {
      delete values.expiryDate;
    }
    if (values.issueDate === '') {
      delete values.issueDate;
    }

    certCrudRef.send({ type: 'ON_SUBMIT', values: values });
  };

  const selectCertName = (certName: string) => {
    certRef.send({ type: 'ON_CERTNAME_SELECT', value: certName });
  };

  return (
    <>
      <TableCard
        mobilePageHeader
        title="Certifications"
        addLabel="Add Certification"
        onAddClick={handleOpen}
        addBtnTag={TAGS.addBtn}
        layoutTag={CT.certs.layout}
      >
        <BasicGrid
          newRecordsTable="certifications"
          data={data}
          loading={crudLoading}
          colDefs={createCertCols({ handleDelete, handleEdit })}
          pagination={true}
          cyTag={CT.certs.table.container}
          noRowsOverlayComponent={CertNoRowsOverlay}
        />
      </TableCard>
      <FormDialog
        title={create ? 'Add Certificate' : 'Edit Certificate '}
        editing={edit}
        creating={create}
        onClose={handleClose}
        padding={'pt-4'}
        bgColor={'white'}
        width={'4xl'}
      >
        <CertForm
          createCert={createCert}
          edit={edit}
          editCert={editCert}
          form={form}
          handleClose={handleClose}
          issuerOptions={issuerOptions}
          selectCertName={selectCertName}
        />
      </FormDialog>
    </>
  );
};
